import { Percentage } from '@ui/components/Amount/Percentage';
import { Pnl } from '@ui/components/Amount/Pnl';
import { TextTooltip } from '@ui/components/TextTooltip';
import { DEFAULT_UNDEFINED_NUMBER_DISPLAY } from '@ui/constants/uiConfig';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import BigNumber from 'bignumber.js';
import { omit } from 'lodash-es';
import { useMemo } from 'react';
import { VStack } from 'styled-system/jsx';
import { SystemStyleObject } from 'styled-system/types';

import { usePositionListContext } from './context';
import { PnLTooltipContent } from './PnLTooltipContent';
import { PositionMarketData } from './types';

export const PositionPnl = ({
  position,
  css,
  isStart = false,
  marketData,
  pnlScale,
}: {
  position: Position;
  css: SystemStyleObject;
  isStart?: boolean;
  marketData?: PositionMarketData;
  pnlScale?: BigNumber;
}) => {
  if (marketData) {
    return (
      <PositionPnlValue
        position={position}
        css={css}
        isStart={isStart}
        marketData={marketData}
        pnlScale={pnlScale}
      />
    );
  }

  return (
    <PositionPnlWithHooks
      position={position}
      css={css}
      isStart={isStart}
      pnlScale={pnlScale}
    />
  );
};

const PositionPnlWithHooks = ({
  position,
  css,
  isStart = false,
  pnlScale,
}: {
  position: Position;
  css: SystemStyleObject;
  isStart?: boolean;
  pnlScale?: BigNumber;
}) => {
  const pairMarketPrice = usePriceLive(position.pair.baseSymbol);

  const { feeConfig } = usePositionListContext();
  const collateralMarketPrice = usePayTokenPriceLatest(
    position.collateralToken,
  );
  const tradePair = useTradePairInfosByPairBase(position.pair.address);
  const marketData = {
    pairMarketPrice,
    collateralMarketPrice,
    tradePair,
  };

  return (
    <PositionPnlValue
      position={position}
      css={css}
      isStart={isStart}
      marketData={marketData}
      feeConfig={feeConfig}
      pnlScale={pnlScale}
    />
  );
};

const PositionPnlValue = ({
  position,
  css,
  isStart = false,
  marketData,
  feeConfig: feeConfigProp,
  pnlScale,
}: {
  position: Position;
  css: SystemStyleObject;
  isStart?: boolean;
  marketData: PositionMarketData;
  feeConfig?: ReturnType<typeof usePositionListContext>['feeConfig'];
  pnlScale?: BigNumber;
}) => {
  const feeConfig = feeConfigProp ?? marketData.tradePair?.feeConfig;
  const pairMarketPrice = marketData.pairMarketPrice;
  const collateralMarketPrice = marketData.collateralMarketPrice;
  const tradePair = marketData.tradePair;

  const pnlAfterFee = useMemo(() => {
    if (!feeConfig || !pairMarketPrice || !collateralMarketPrice || !tradePair)
      return;

    const value = position.getPnlAfterFee({
      ...feeConfig,
      pairMarketPrice,
      collateralMarketPrice,
      tradePair,
    });

    return pnlScale ? value.multipliedBy(pnlScale) : value;
  }, [
    collateralMarketPrice,
    feeConfig,
    pairMarketPrice,
    pnlScale,
    position,
    tradePair,
  ]);

  const pnlAfterFeePercentage = useMemo(() => {
    if (!feeConfig || !pairMarketPrice || !collateralMarketPrice || !tradePair)
      return;

    return position.getPnlAfterFeePercentage({
      ...feeConfig,
      pairMarketPrice,
      collateralMarketPrice,
      tradePair,
    });
  }, [collateralMarketPrice, feeConfig, pairMarketPrice, position, tradePair]);

  const isPendingPosition = position.isPendingPosition;

  if (isPendingPosition) return DEFAULT_UNDEFINED_NUMBER_DISPLAY;

  return (
    <TextTooltip
      content={
        <PnLTooltipContent position={position} marketData={marketData} />
      }
    >
      <VStack alignItems={isStart ? 'flex-start' : 'flex-end'} gap={1}>
        <Pnl
          value={pnlAfterFee}
          css={omit(css, 'color')}
          opts={{
            isShowSign: true,
          }}
          data-cy="Pnl"
          data-cy-value={pnlAfterFee}
        />
        {pnlAfterFeePercentage !== undefined && (
          <Percentage
            textStyle="numbers.xsmall"
            value={pnlAfterFeePercentage}
            textDecoration="none"
            css={{ textDecoration: 'none' }}
            opts={{
              isShowSign: true,
            }}
            data-cy="PnLToolTip-PnLAfterFeePercentage"
            data-cy-value={pnlAfterFeePercentage}
          />
        )}
      </VStack>
    </TextTooltip>
  );
};
